import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMyTeacherId, getCurrentSubscription, getPlans, getBillingHistory,
  createCheckoutSession, cancelSubscription as cancelSubscriptionRequest,
} from '../utils/subscriptionService';

/**
 * Single source of truth for "does this teacher have an active NOVAS
 * subscription" — both for the Payments and plans tab and, later, for any
 * paywall check elsewhere in the app (isActive/isTrialing/isPastDue below
 * are exactly the flags a paywall would gate on).
 */
export function useSubscription() {
  const queryClient = useQueryClient();

  const teacherIdQuery = useQuery({
    queryKey: ['myTeacherId'],
    queryFn: getMyTeacherId,
    staleTime: Infinity,
  });
  const teacherId = teacherIdQuery.data ?? null;

  const subscriptionQuery = useQuery({
    queryKey: ['subscription', teacherId],
    queryFn: () => getCurrentSubscription(teacherId),
    enabled: !!teacherId,
  });

  const plansQuery = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: getPlans,
    staleTime: 5 * 60 * 1000,
  });

  const historyQuery = useQuery({
    queryKey: ['billingHistory', teacherId],
    queryFn: () => getBillingHistory(teacherId),
    enabled: !!teacherId,
  });

  const subscription = subscriptionQuery.data ?? null;
  const status = subscription?.status ?? null;

  const startCheckout = async (planCode) => {
    const url = await createCheckoutSession(planCode);
    window.location.href = url;
  };

  const cancel = async () => {
    await cancelSubscriptionRequest();
    // The webhook updates the row asynchronously once Stripe confirms —
    // refetch shortly after so cancel_at_period_end shows up without a
    // full page reload.
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['subscription', teacherId] });
    }, 1500);
  };

  return {
    teacherId,
    subscription,
    plan: subscription?.plan ?? null,
    plans: plansQuery.data ?? [],
    billingHistory: historyQuery.data ?? [],
    isActive: status === 'active' || status === 'trialing',
    isTrialing: status === 'trialing',
    isPastDue: status === 'past_due' || status === 'unpaid',
    isCanceled: status === 'canceled',
    hasNoSubscription: !subscriptionQuery.isLoading && !subscription,
    isLoading: teacherIdQuery.isLoading || subscriptionQuery.isLoading,
    startCheckout,
    cancel,
  };
}
